import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useParams } from 'react-router-dom'
import {
    fetchEmployeeLeave,
    fetchEmployeeLeaveBalance,
    fetchLeaveDetails,
    updateApprovedByLeave,
    updateCheckedByLeave,
    updateFinalApprovedByLeave,
    updateLeaveDeclined
} from '../../../services/LeaveService'

const API_MAP = {
    checker: updateCheckedByLeave,
    first_approver: updateApprovedByLeave,
    final_approver: updateFinalApprovedByLeave,
    declined: updateLeaveDeclined
}

const LeaveDetailNoAction = () => {
    const { leaveId } = useParams()
    const [leave, setLeave] = useState(null)
    const [loading, setLoading] = useState(true)
    const [message, setMessage] = useState({ type: '', text: '', isOpen: false })
    const [modalState, setModalState] = useState({ isOpen: false, action: '' })
    const [declineReason, setDeclineReason] = useState('')
    const [role, setRole] = useState('')
    const [isAssignedUser, setIsAssignedUser] = useState(false)

    useEffect(() => {
        fetchData()
    }, [leaveId])

    const fetchData = async () => {
        try {
            const data = await fetchLeaveDetails(leaveId)
            if (!data?.leave) throw new Error('Leave not found')

            const sessionUserId = localStorage.getItem('id')?.split('_')[1]
            const assigned = data.leave.assignedUser?.id == sessionUserId
            const role = getRole(sessionUserId, data.leave)
            setLeave({
                ...data.leave,
                extendedProps: {
                    fullname: `${data.leave.employee?.firstName || 'N/A'} ${data.leave.employee?.lastName || 'N/A'}`,
                    description: data.leave.reason || 'No description',
                    status: data.leave.status || 'Unknown',
                    duration: data.extendedProps.duration || 'N/A'
                }
            })

            setIsAssignedUser(assigned)
            setRole(role)
        } catch (error) {
            console.error(error)
            setMessage({ type: 'error', text: error.message || 'Failed to fetch leave details.', isOpen: true })
        } finally {
            setLoading(false)
        }
    }

    const handleStatusChange = async (status, approverRole = '') => {
        if (!leave?.id) return setErrorMessage('Leave request does not exist.')
        if (status === 'DECLINED' && !declineReason) return setErrorMessage('Please enter a reason for declining.')

        setLoading(true)
        try {
            const request = {
                id: leave.id,
                remarks: status === 'DECLINED' ? declineReason : 'Approved',
                status
            }

            const apiCall = API_MAP[approverRole]
            if (apiCall) await apiCall(request)

            setMessage({ type: 'success', text: `Leave request ${status.toLowerCase()} successfully!`, isOpen: true })
            resetModal()
            fetchData()
        } catch (error) {
            console.error(error)
            setErrorMessage(error.message || 'Failed to update leave status.')
        } finally {
            setLoading(false)
        }
    }

    const setErrorMessage = (text) => setMessage({ type: 'error', text, isOpen: true })
    const resetModal = () => {
        setModalState({ isOpen: false, action: '' })
        setDeclineReason('')
    }

    if (loading) return <LoadingMessage text="Loading leave details..." />
    if (!leave) return <LoadingMessage text="Failed to load leave details." isError />

    return (
        <div className="flex justify-center">
            <div className="w-full max-w-3xl bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between border-b pb-4 mb-4">
                    <h2 className="text-xl font-semibold">LEAVE REQUEST FOR {leave.extendedProps?.fullname}</h2>

                    <span
                        className={`px-2 py-1 rounded text-sm font-medium ${
                            leave.extendedProps?.status === 'APPROVED'
                                ? 'bg-green-100 text-green-700'
                                : leave.extendedProps?.status === 'PENDING'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-700'
                        }`}
                    >
                        {leave.extendedProps?.status || 'N/A'}
                    </span>
                </div>
                {message.isOpen && (
                    <MessageDialog
                        type={message.type}
                        text={message.text}
                        onClose={() => setMessage((prev) => ({ ...prev, isOpen: false }))}
                    />
                )}

                <LeaveDetailsSection leave={leave} />

                <LeaveActions
                    title="Checked By"
                    leave={leave?.checkedBy}
                    verifiedAt={formatDateTime(leave?.verifiedCheckedBy)}
                />
                <LeaveActions
                    title="Approved By"
                    leave={leave?.approvedBy}
                    verifiedAt={formatDateTime(leave?.verifiedApprovedBy)}
                />
                <LeaveActions
                    title="Final Approved By"
                    leave={leave?.lastApprovedBy}
                    verifiedAt={formatDateTime(leave?.verifiedLastApprovedBy)}
                />

                {modalState.isOpen && (
                    <DeclineModal
                        declineReason={declineReason}
                        setDeclineReason={setDeclineReason}
                        onClose={resetModal}
                        onConfirm={() => handleStatusChange(modalState.action, 'declined')}
                    />
                )}
            </div>
        </div>
    )
}

// =============== COMPONENTS =============== //

const LoadingMessage = ({ text, isError = false }) => (
    <p className={`text-center ${isError ? 'text-red-500' : 'text-gray-500'}`}>{text}</p>
)

const MessageDialog = ({ type, text, onClose }) => (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className={`text-lg font-medium ${type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {type === 'success' ? 'Success' : 'Error'}
            </h3>
            <p className="mt-2 text-black">{text}</p>
            <div className="flex justify-end mt-4">
                <button className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600" onClick={onClose}>
                    Close
                </button>
            </div>
        </div>
    </div>
)

const LeaveDetailsSection = ({ leave }) => (
    <div className="text-sm border-b pb-4 mb-4 space-y-2">
        {Object.entries({
            Type: leave.type.replace('_', ' '),
            Reason: leave.reason || 'No description',
            Date: `${leave.start} to ${leave.end}`,
            Duration: leave.extendedProps?.duration || 'N/A',
            'Internal Remarks': leave.internalRemarks || 'N/A',
            Remarks: leave.remarks || 'N/A',
            // Status: leave.extendedProps?.status || 'N/A',
            'Currently Assigned To': leave.assignedUser
                ? `${leave.assignedUser.firstName} ${leave.assignedUser.lastName}`
                : 'N/A'
        }).map(([label, value]) => (
            <p key={label}>
                <strong>{label}:</strong> {value}
            </p>
        ))}
    </div>
)

const LeaveActions = ({ title, leave, verifiedAt }) => (
    <div className="text-sm border-b pb-4 mb-4 space-y-2">
        <h4 className="font-medium">{title}:</h4>

        <p>
            <strong>Name:</strong> {leave ? `${leave.firstName || 'N/A'} ${leave.lastName || 'N/A'}` : 'N/A'}
        </p>

        <p className="text-gray-500 text-sm">
            <strong>Verified:</strong> {verifiedAt}
        </p>
    </div>
)

const DeclineModal = ({ declineReason, setDeclineReason, onClose, onConfirm }) => (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium">Decline Leave Request</h3>
            <textarea
                className="mt-4 w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Enter reason..."
                rows="3"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
            />
            <div className="flex justify-end mt-4 space-x-2">
                <button className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400" onClick={onClose}>
                    Cancel
                </button>
                <button className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600" onClick={onConfirm}>
                    Confirm Decline
                </button>
            </div>
        </div>
    </div>
)

const getRole = (userId, leave) => {
    if (userId == leave?.checkedBy?.id) return 'checker'
    if (userId == leave?.approvedBy?.id) return 'first_approver'
    if (userId == leave?.lastApprovedBy?.id) return 'final_approver'
    return 'N/A'
}

const formatDateTime = (dateTime) => (dateTime ? moment(dateTime).format('MMMM DD, YYYY [at] hh:mm A') : 'PENDING')

export default LeaveDetailNoAction
